/**
 * RESPONSIBILITY: the @Choice control whose vocabulary lives in the document.
 *
 * `in` names where the allowed values are — `categories` at the section root, `./kinds`
 * on the same row. They are read when this renders, never baked into the schema, so an
 * admin adding a category is visible here without a deploy.
 *
 * Registers with a tester + rank. The highest-ranked renderer for a schema node wins, so
 * adding a control is a registration — there is no switch on field type.
 */
import { rankWith, optionIs, type ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { resolveOptions } from '@framework/core';

function LookupControlBase({ data, handleChange, path, label, uischema, errors }: ControlProps) {
  const opts = (uischema as { options?: Record<string, unknown> }).options ?? {};
  const multiple = Boolean(opts.multiple);

  // The whole document, because `in` is resolved against the section root and this
  // control only ever knows its own value.
  const root = (useJsonForms().core?.data ?? {}) as Record<string, unknown>;
  const allowed = opts.in
    ? resolveOptions(String(opts.in), root)
    : ((opts.fixed as string[] | undefined) ?? []);

  const held: string[] = multiple
    ? (Array.isArray(data) ? (data as string[]) : [])
    : (data ? [String(data)] : []);

  const toggle = (v: string) =>
    handleChange(path, multiple
      ? (held.includes(v) ? held.filter((x) => x !== v) : [...held, v])
      : v);

  return (
    <div className="ctl">
      <label className="ctl-label">{label}</label>

      {allowed.length === 0 ? (
        <p className="ctl-hint">
          Nothing to choose from yet — add values to <code>{String(opts.in ?? '')}</code> first.
        </p>
      ) : multiple ? (
        <div className="ctl-chips">
          {allowed.map((v) => (
            <button
              key={v} type="button"
              className={held.includes(v) ? 'ctl-chip ctl-chip-on' : 'ctl-chip'}
              aria-pressed={held.includes(v)}
              onClick={() => toggle(v)}
            >{v}</button>
          ))}
        </div>
      ) : (
        <select
          className="ctl-input" value={held[0] ?? ''}
          onChange={(e) => handleChange(path, e.target.value)}
        >
          <option value="">—</option>
          {allowed.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      )}

      {errors && <p className="ctl-error">{errors}</p>}
    </div>
  );
}

export const lookupControl = {
  tester: rankWith(10, optionIs('widget', 'lookup')),
  renderer: withJsonFormsControlProps(LookupControlBase),
};
